import {PrimeIcons} from "primereact/api";
import {Knob} from "primereact/knob";

import type {ValueOrderData} from "@reva/shared/value-order";

import {DetailsPanel, DetailsPanelRow, DetailsPanelSeparator} from "../../component/panels";
import {useRevaTranslation} from "../../hooks/languageHooks";
import {formatDate, formatDateAndTime, isDateBeforeDate} from "../../util/dateUtil";

interface PanelStatusProps {
    orderData: ValueOrderData;
}

export function PanelStatus({orderData}: PanelStatusProps) {
    const {t, language} = useRevaTranslation();

    const orderDetails = orderData?.orderDetails;

    let statusClassName = "";
    let canExpire = true;
    switch (orderDetails?.processingStatus?.code) {
        case "Completed":
            canExpire = false;
            statusClassName = "OrderStatusCompleted";
            break;
        case "Cancelled":
            canExpire = false;
            statusClassName = "OrderStatusCancelled";
            break;
        case "ClientActionRequired":
            canExpire = false;
            break;
        default:
            break;
    }
    const expired = canExpire && isDateBeforeDate(orderDetails?.deadline);
    if (expired && !statusClassName) {
        statusClassName = "OrderStatusExpired";
    }

    return (
        <DetailsPanel icon={PrimeIcons.CALENDAR} title={t("order.status.title")}>
            {!!orderDetails?.processingStatus && (
                <>
                    <div className="w-full flex flex-row items-center">
                        <Knob
                            value={orderDetails?.processingStatus.percent}
                            readOnly
                            valueTemplate="{value}%"
                            className={`mr-8 ${statusClassName}`}
                            size={60}
                        />
                        <div className={`OrderStatus flex-1 ${statusClassName}`}>
                            {orderDetails?.processingStatus.name}
                        </div>
                    </div>

                    <DetailsPanelSeparator />
                </>
            )}

            <DetailsPanelRow
                label={t("order.status.creationDate")}
                value={formatDateAndTime(orderDetails?.creationDate, language)}
            />
            <DetailsPanelRow
                label={t("order.status.inspectionDate")}
                value={formatDate(orderDetails?.inspectionDate, language)}
            />
            <DetailsPanelRow
                label={t("order.status.deadline")}
                value={formatDate(orderDetails?.deadline, language)}
                valueClassName={expired ? "DeadlineExpired" : ""}
            />
            <DetailsPanelRow
                label={t("order.status.completionDate")}
                value={formatDate(orderDetails?.completionDate, language)}
            />
            <DetailsPanelRow
                label={t("order.status.processingCompletedDate")}
                value={formatDate(orderDetails?.processingCompletedDate, language)}
            />
            <DetailsPanelRow
                label={t("order.status.cancellationDate")}
                value={formatDate(orderDetails?.cancellationDate, language)}
            />
        </DetailsPanel>
    );
}
